import { useQuery } from '@tanstack/react-query';

import { fetchJson } from '../api.ts';

/**
 * WorkOS widget kinds we support. Must stay in sync with the backend's
 * `widgetTokenBody` enum in `apps/api/src/routes/auth.ts`.
 *
 * Widget tokens are scoped per kind on the server; the SPA passes the
 * widget name + the active TA org slug and the backend resolves the
 * WorkOS org id + membership in one shot.
 */
export type WidgetKind =
  | 'user-management'
  | 'org-switcher'
  | 'user-profile'
  | 'user-security'
  | 'user-sessions';

interface WidgetTokenResponse {
  token: string;
}

/**
 * Returns a function suitable for a widget's `authToken` prop. Each
 * call fetches a fresh token. WorkOS widget tokens live ~1h; this
 * implementation re-fetches on every call (the widget invokes the
 * callback when its token expires).
 *
 * @param orgSlug — the TA org slug; the backend resolves the WorkOS
 *                  org id and validates membership.
 */
export function makeAuthTokenGetter(orgSlug: string, widget: WidgetKind): () => Promise<string> {
  return async () => {
    const res = await fetchJson<WidgetTokenResponse>('/v1/auth/tokens/widget', {
      method: 'POST',
      body: JSON.stringify({ widget, org_slug: orgSlug }),
    });
    return res.token;
  };
}

/**
 * Convenience hook for callers that want a Promise-of-token (e.g.
 * `<UsersManagement authToken={tokenPromise} />` would need this).
 * Most call sites can use `makeAuthTokenGetter` directly.
 */
export function useWidgetToken(orgSlug: string | null, widget: WidgetKind) {
  return useQuery({
    queryKey: ['widget-token', widget, orgSlug],
    enabled: orgSlug != null,
    queryFn: async () => {
      if (!orgSlug) throw new Error('no org slug');
      return makeAuthTokenGetter(orgSlug, widget)();
    },
    // Refetch a little before the WorkOS ~1h expiry.
    staleTime: 50 * 60 * 1000,
  });
}
